import CodeMirror from "@uiw/react-codemirror";
import { cpp } from "@codemirror/lang-cpp";
import { EditorView } from "@codemirror/view";
import { HighlightStyle, syntaxHighlighting } from "@codemirror/language";
import { tags as t } from "@lezer/highlight";

// Editor chrome — backgrounds, gutters, selection. Foreground colors come
// from the highlight style below, not from this theme.
const chrome = EditorView.theme(
  {
    "&": { backgroundColor: "var(--bg)", color: "var(--fg)" },
    ".cm-content": { caretColor: "var(--accent)" },
    ".cm-cursor, .cm-dropCursor": { borderLeftColor: "var(--accent)" },
    ".cm-gutters": {
      backgroundColor: "var(--bg)",
      color: "var(--fg-4)",
      border: "none",
      borderRight: "1px solid var(--line)",
    },
    ".cm-activeLine": { backgroundColor: "var(--bg-1)" },
    ".cm-activeLineGutter": { backgroundColor: "var(--bg-1)", color: "var(--fg-3)" },
    "&.cm-focused .cm-selectionBackground, ::selection": {
      backgroundColor: "var(--accent-soft) !important",
    },
    "&.cm-focused": { outline: "none" },
  },
  { dark: true },
);

// GLSL highlight palette — keeps the terminal-dark accent system: keywords
// glow accent, types are dimmer accent, literals are warm, comments dim out.
const palette = HighlightStyle.define([
  { tag: t.keyword, color: "var(--accent)", fontWeight: "500" },
  { tag: t.controlKeyword, color: "var(--accent)", fontWeight: "500" },
  { tag: t.modifier, color: "var(--accent-dim)" },
  { tag: [t.typeName, t.standard(t.typeName)], color: "var(--accent-dim)" },
  { tag: t.definition(t.typeName), color: "var(--accent-dim)" },
  { tag: t.function(t.variableName), color: "#dfe9e2" },
  { tag: t.definition(t.function(t.variableName)), color: "#f1ffe9", fontWeight: "500" },
  { tag: t.variableName, color: "var(--fg-2)" },
  { tag: t.propertyName, color: "var(--fg-2)" },
  { tag: t.number, color: "oklch(0.85 0.13 80)" },
  { tag: t.string, color: "oklch(0.85 0.13 80)" },
  { tag: t.operator, color: "var(--fg-3)" },
  { tag: t.punctuation, color: "var(--fg-3)" },
  { tag: t.bracket, color: "var(--fg-3)" },
  { tag: t.comment, color: "var(--fg-4)", fontStyle: "italic" },
  { tag: t.lineComment, color: "var(--fg-4)", fontStyle: "italic" },
  { tag: t.meta, color: "var(--fg-3)" },
  { tag: t.processingInstruction, color: "var(--accent-dim)" },
]);

type Props = {
  value: string;
  onChange?: (v: string) => void;
  minHeight?: string;
  maxHeight?: string;
  readOnly?: boolean;
};

// CodeMirror 6 + C++ grammar — close enough for GLSL syntax (per
// design/handoff/README.md: "GLSL mode (or Monaco if you prefer)").
export function GlslEditor({
  value,
  onChange,
  minHeight = "280px",
  maxHeight,
  readOnly = false,
}: Props) {
  return (
    <CodeMirror
      value={value}
      onChange={onChange}
      readOnly={readOnly}
      editable={!readOnly}
      extensions={[cpp(), chrome, syntaxHighlighting(palette), EditorView.lineWrapping]}
      basicSetup={{
        lineNumbers: !readOnly,
        highlightActiveLine: !readOnly,
        highlightActiveLineGutter: !readOnly,
        foldGutter: false,
      }}
      minHeight={minHeight}
      maxHeight={maxHeight}
    />
  );
}
